import { useEffect, useState } from 'react'

import { isArray } from 'lodash'
import { useSelector } from 'react-redux'

import { getIdOrNull, isOptimistic } from '../..'
import { getInstitutionSectors } from '../institution'
import { SectorContexts } from '../sector'
import { isInstitutionUser } from '../../roles'
import { selectUser } from '../../../reducers/UserReducer'

/**
 * Strict mode is what asks the API for the caller's own units rather than the whole institution's.
 * A local admin administers a set of units and may only send a request to one of them, so the list
 * it picks from is its own scope — the backend refuses the others anyway. A school chooses among
 * all the units of a place it may send students to, and a student is not scoped at all: both read
 * the institution whole.
 */
const buildParameters = user => ({
  archived: false, strictMode: isInstitutionUser(user), orderBy: 'name', contexts: [SectorContexts.ACCEPTED_SECTIONS]
})

/**
 * Returns the selectable sectors of an institution, ordered by name, along with the
 * setter, so a caller creating a sector of its own can seat it in the list at once.
 *
 * The list is emptied as soon as the institution changes, so the sectors of the
 * previous one are never offered, and a response landing out of order is dropped.
 *
 * The institution is compared by identity, so pass a stable entity rather than
 * an object built inline, which would refetch on every render.
 */
const useInstitutionSectors = institution => {
  const user = useSelector(selectUser)

  const [sectors, setSectors] = useState([])

  useEffect(() => {
    let active = true

    setSectors(prev => (prev.length === 0 ? prev : []))

    if (getIdOrNull(institution) !== null && !isOptimistic(institution)) {
      getInstitutionSectors(user, institution, buildParameters(user))
        .then(({ data }) => {
          if (active) {
            setSectors(isArray(data) ? data : [])
          }
        })
        .catch(error => {
          console.error(error)

          if (active) {
            setSectors(prev => (prev.length === 0 ? prev : []))
          }
        })
    }

    return () => {
      active = false
    }
  }, [user, institution])

  return [sectors, setSectors]
}

export default useInstitutionSectors
